// src/utils/groupModeHelpers.ts
//
// Pure utility functions for group-mode awareness.
//
// No React dependencies — safe to import from any hook, component, or test.
// All functions operate on store types only.
import type { ISelectedWell } from "stores/map";

/**
 * Expands the entries of `selectedWells` to a deduplicated list of individual
 * well UWIs suitable for sending to backend services (XDA, map entities, etc.).
 *
 * **Single mode:** `selectedWells` keys are real well UWIs. The canonical
 * `well.Uwi` field is used (not the map key) to ensure consistency in case
 * the store key and `Uwi` ever diverge due to normalisation or encoding.
 *
 * **Group mode:** An `ISelectedWell` entry carries `memberUwis` (populated at
 * click time by `useMapClickSelection`). This function flattens all member UWIs
 * across every selected group into a single deduplicated list.
 *
 * Deduplication guards against the case where two selected groups share a member
 * well (e.g., due to binning overlap), which would otherwise cause the same UWI
 * to appear twice in a backend request.
 */
export function expandSelectedWellsToUwis(
  selectedWells: Readonly<Record<string, ISelectedWell>>
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const well of Object.values(selectedWells)) {
    if (well.memberUwis && well.memberUwis.length > 0) {
      // Group mode entity — expand to constituent UWIs
      for (const uwi of well.memberUwis) {
        if (!seen.has(uwi)) {
          seen.add(uwi);
          result.push(uwi);
        }
      }
    } else {
      // Single mode entity — use the canonical Uwi field, not the map key,
      // so callers always receive the authoritative identifier.
      const uwi = well.Uwi;
      if (uwi && !seen.has(uwi)) {
        seen.add(uwi);
        result.push(uwi);
      }
    }
  }

  return result;
}

/**
 * Normalises the legacy `xdaLineRequest.wells` value into a string array.
 * Older call sites sometimes wrote a selected-wells record into this field even
 * though the store type is `string[]`; accepting both keeps the payload builder
 * resilient while the line-selection path is cleaned up.
 */
export function normalizeXdaLineRequestWells(
  wells: readonly string[] | Readonly<Record<string, unknown>> | null | undefined
): string[] {
  if (!wells) {
    return [];
  }

  if (Array.isArray(wells)) {
    return wells.filter(
      (uwi): uwi is string => typeof uwi === "string" && uwi.length > 0
    );
  }

  return Object.keys(wells).filter((uwi) => uwi.length > 0);
}

export function getXdaPayloadWells(
  selectedWells: Readonly<Record<string, ISelectedWell>>,
  lineRequestWells:
    | readonly string[]
    | Readonly<Record<string, unknown>>
    | null
    | undefined
): string[] {
  const selectedWellUwis = expandSelectedWellsToUwis(selectedWells);
  const intersectWells = normalizeXdaLineRequestWells(lineRequestWells);

  if (selectedWellUwis.length === 0) {
    return intersectWells;
  }

  // Union selected + intersection wells so that intersection wells not explicitly
  // selected still have polygon data in the API response. Filter-promotion blocks
  // in renderScene promote them to colored; if removed from the filter, drawShape
  // skips them (they do not appear as ghost crosshatch polygons).
  const seen = new Set(selectedWellUwis);
  const result = [...selectedWellUwis];
  for (const uwi of intersectWells) {
    if (!seen.has(uwi)) {
      result.push(uwi);
    }
  }
  return result;
}

/**
 * Returns `true` if any entry in `selectedWells` represents a group entity
 * (i.e., has a non-empty `memberUwis` array).
 */
export function isGroupModeSelection(
  selectedWells: Readonly<Record<string, ISelectedWell>>
): boolean {
  return Object.values(selectedWells).some(
    (w) => w.memberUwis != null && w.memberUwis.length > 0
  );
}
